// views/Admin/Statistics/components/Charts/BeaconCharts/QrScans/QrScansByDaysChart/QrScansByDaysChart.tsx
import { useState, useMemo, useCallback, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import styles from './QrScansByDaysChart.module.css';

const PALETTE = [
  '#4f6ef7', '#f75f6e', '#f7a84f', '#4fd6f7', '#a84ff7',
  '#4ff7a0', '#f74fc8', '#f7e24f', '#4fb3f7', '#f76b4f',
  '#7af74f', '#f74f8e', '#4ff7d6', '#c8f74f', '#7b4ff7',
  '#f7c44f', '#4f9ef7', '#f74f4f', '#4ff77a', '#d64ff7',
  '#f79e4f', '#4f6ef7', '#f74fb3', '#b3f74f', '#4fe8f7',
  '#f76ef7', '#6ef74f', '#f74f6e', '#4f4ff7',
];

type ColorMap = Record<string, string>;
interface Segment { device_id: string; count: number; color: string; }
interface DayData { date: string; label: string; total: number; segments: Segment[]; }

function buildColorMap(tableData: any): ColorMap {
  const seen = new Set<string>();
  const map: ColorMap = {};
  tableData.device_id.forEach((id: string) => {
    if (!seen.has(id)) {
      seen.add(id);
      map[id] = PALETTE[seen.size - 1] ?? '#888';
    }
  });
  return map;
}

function toLocalDateKey(ts: number): string {
  const d = new Date(ts * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function processData(tableData: any, colorMap: ColorMap): DayData[] {
  const byDay: Record<string, Record<string, number>> = {};

  tableData.timestamp.forEach((t: number, i: number) => {
    const key = toLocalDateKey(t).slice(0, 10);
    if (!byDay[key]) byDay[key] = {};
    const dev = tableData.device_id[i];
    byDay[key][dev] = (byDay[key][dev] || 0) + 1;
  });

  return Object.entries(byDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, devMap]) => {
      const segments: Segment[] = Object.entries(devMap)
        .map(([device_id, count]) => ({ device_id, count, color: colorMap[device_id] }))
        .sort((a, b) => b.count - a.count);
      const total = segments.reduce((s, seg) => s + seg.count, 0);
      const label = new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      return { date, label, total, segments };
    });
}

interface TooltipState { x: number; y: number; day: DayData; }

const QrScansByDaysChart = ({ table }: { table: any }) => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const { days, maxTotal, grandTotal } = useMemo(() => {
    if (!table) return { days: [] as DayData[], maxTotal: 0, grandTotal: 0 };
    const colorMap = buildColorMap(table);
    const days = processData(table, colorMap);
    const maxTotal = Math.max(...days.map((d) => d.total));
    const grandTotal = days.reduce((s, d) => s + d.total, 0);
    return { days, maxTotal, grandTotal };
  }, [table]);

  const yTicks = useMemo(() => {
    if (!maxTotal) return [] as number[];
    const step = maxTotal <= 10 ? 2 : maxTotal <= 20 ? 5 : 10;
    const ticks: number[] = [];
    for (let v = 0; v <= maxTotal; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] < maxTotal) ticks.push(maxTotal);
    return ticks;
  }, [maxTotal]);

  const handleMouseEnter = useCallback((e: MouseEvent, day: DayData) => setTooltip({ x: e.clientX, y: e.clientY, day }), []);
  const handleMouseMove = useCallback((e: MouseEvent) => setTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null)), []);
  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  const CHART_H = 320;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.title}>Referral code · validations</p>
          <h2 className={styles.subtitle}>Daily device activity</h2>
        </div>
        <div className={styles.totalBadge}>
          <span className={styles.totalLabel}>Total validations</span>
          <span className={styles.totalValue}>{grandTotal}</span>
        </div>
      </div>

      <div className={styles.chartArea}>
        <div className={styles.yAxis}>
          {yTicks.map((v) => (
            <span key={v} className={styles.yTick}>{v}</span>
          ))}
        </div>

        <div className={styles.chartInner}>
          <div className={styles.gridLines}>
            {yTicks.map((v) => (
              <div key={v} className={styles.gridLine} style={{ bottom: `${(v / maxTotal) * 100}%` }} />
            ))}
          </div>

          <div className={styles.barsRow}>
            {days.map((day) => (
              <div key={day.date} className={styles.dayColumn}>
                <div className={styles.barZone}>
                  <div
                    className={styles.stackWrapper}
                    onMouseEnter={(e) => handleMouseEnter(e, day)}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  >
                    {day.segments.map((seg) => (
                      <div
                        key={seg.device_id}
                        className={styles.segment}
                        style={{ height: `${(seg.count / maxTotal) * CHART_H}px`, backgroundColor: seg.color }}
                      />
                    ))}
                  </div>
                </div>
                <div className={styles.dayLabel}>{day.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {tooltip &&
        createPortal(
          <div className={styles.tooltip} style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, calc(-100% - 12px))' }}>
            <div className={styles.tooltipDate}>{tooltip.day.label}</div>
            <div className={styles.tooltipTotal}>{tooltip.day.total} validations total</div>
            <div className={styles.tooltipRows}>
              {tooltip.day.segments.map((seg) => (
                <div key={seg.device_id} className={styles.tooltipRow}>
                  <div className={styles.tooltipSwatch} style={{ backgroundColor: seg.color }} />
                  <span className={styles.tooltipDeviceId}>{seg.device_id}</span>
                  <span className={styles.tooltipCount}>{seg.count}</span>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default QrScansByDaysChart;
